/**
 * Assembly Service Component (global)
 * Single button: add/remove Montage service for ALL products in cart.
 */

import { manageAssemblyProduct, updateCartSections } from './assembly-service-manager.js';

const ASSEMBLY_VARIANT_ID = '56073035743612';
const LOGISTICS_VARIANT_ID = '55982063812988';

function getAssemblyVariantId() {
  if (typeof window !== 'undefined' && window.ASSEMBLY_VARIANT_ID) return window.ASSEMBLY_VARIANT_ID;
  if (typeof document !== 'undefined') {
    const attr = document.documentElement.getAttribute('data-assembly-variant-id') ||
                  document.body?.getAttribute('data-assembly-variant-id');
    if (attr) return attr;
  }
  return ASSEMBLY_VARIANT_ID || null;
}

if (!customElements.get('assembly-service-component')) {
  const AssemblyServiceComponent = class extends HTMLElement {
    constructor() {
      super();
      this.boundOnToggle = this.onToggle.bind(this);
      this.initialized = false;
    }

    connectedCallback() {
      if (!this.getAttribute('data-assembly-all')) return;

      try {
        if (this.initialized && this.checkbox) {
          this.checkbox.removeEventListener('change', this.boundOnToggle);
        }

        this.checkbox = this.querySelector('input[type="checkbox"]');
        this.isUpdating = false;

        if (!this.checkbox) {
          this.initialized = false;
          return;
        }

        this.checkbox.addEventListener('change', this.boundOnToggle);

        this.boundClick = this.handleClick.bind(this);
        this.addEventListener('click', this.boundClick, true);

        this.syncStateFromCart();
        this.initialized = true;
      } catch (error) {
        console.error('[Assembly Service] Error in connectedCallback:', error);
        this.initialized = false;
      }
    }

    disconnectedCallback() {
      if (this.checkbox && this.boundOnToggle) {
        this.checkbox.removeEventListener('change', this.boundOnToggle);
      }
      if (this.boundClick) {
        this.removeEventListener('click', this.boundClick, true);
      }
      this.initialized = false;
    }

    handleClick(e) {
      const target = e.target;
      if (target === this.checkbox) return;
      const label = this.querySelector('.assembly-service-toggle');
      const button = this.querySelector('.assembly-service-button');
      if (!label || !button || !target || !label.contains(target)) return;
      e.preventDefault();
      e.stopPropagation();
      if (this.isUpdating || !this.checkbox) return;
      this.checkbox.checked = !this.checkbox.checked;
      this.onToggle(new Event('change'));
    }

    /**
     * Fetch cart and set checkbox/label from whether all eligible items have assembly
     */
    async syncStateFromCart() {
      try {
        const cart = await this.fetchCart();
        if (!cart?.items?.length) return;

        const assemblyVariantId = getAssemblyVariantId();
        const eligible = cart.items.filter((item) => {
          const vid = String(item.variant_id);
          if (assemblyVariantId && vid === String(assemblyVariantId)) return false;
          if (vid === LOGISTICS_VARIANT_ID) return false;
          return true;
        });

        if (eligible.length === 0) {
          if (this.checkbox) this.checkbox.disabled = true;
          return;
        }

        const withAssembly = eligible.filter((item) => item.properties?.['_assembly-service'] === '1');
        const allHaveAssembly = withAssembly.length === eligible.length;

        if (this.checkbox) {
          this.checkbox.disabled = false;
          this.checkbox.checked = allHaveAssembly;
        }
        this.updateToggleText(allHaveAssembly);
      } catch (e) {
        console.warn('[Assembly Service] Could not sync state from cart:', e);
      }
    }

    async fetchCart() {
      const url = typeof theme !== 'undefined' && theme.routes?.cart_url ? theme.routes.cart_url : '/cart.js';
      const config = typeof theme !== 'undefined' && theme.utils?.fetchConfig
        ? theme.utils.fetchConfig('json', 'GET')
        : { method: 'GET', headers: { 'Content-Type': 'application/json' } };
      const response = await fetch(url, config);
      if (!response.ok) throw new Error('Failed to fetch cart');
      return response.json();
    }

    async onToggle(event) {
      if (this.isUpdating) {
        event.preventDefault();
        return;
      }

      const isEnabled = this.checkbox.checked;
      const newValue = isEnabled ? '1' : '0';

      this.showLoading();
      this.disableCartInteractions();

      try {
        const updatedCartData = await this.updateAssemblyForAllItems(newValue);
        this.updateToggleText(isEnabled);

        const sectionsToUpdate = this.collectSectionsToUpdate();
        const updatedSections = await manageAssemblyProduct(updatedCartData, sectionsToUpdate, true);

        if (updatedSections && Object.keys(updatedSections).length > 0) {
          updateCartSections(updatedSections, sectionsToUpdate);
        }

        this.hideLoading();
        this.enableCartInteractions();
      } catch (error) {
        console.error('[Assembly Service] Error updating assembly for all:', error);
        this.checkbox.checked = !isEnabled;
        this.updateToggleText(!isEnabled);
        this.hideLoading();
        this.enableCartInteractions();
      }
    }

    /**
     * Set _assembly-service to value for every eligible cart item.
     * Shopify has no bulk property API, so we must call cart/change.js once per item.
     */
    async updateAssemblyForAllItems(value) {
      const cart = await this.fetchCart();
      const assemblyVariantId = getAssemblyVariantId();
      const sectionsToUpdate = this.collectSectionsToUpdate();
      const sectionsParam = Array.from(sectionsToUpdate).join(',');

      const eligible = cart.items.filter((item) => {
        const vid = String(item.variant_id);
        if (assemblyVariantId && vid === String(assemblyVariantId)) return false;
        if (vid === LOGISTICS_VARIANT_ID) return false;
        return true;
      });

      const changeConfig = typeof theme !== 'undefined' && theme.utils?.fetchConfig
        ? theme.utils.fetchConfig('json')
        : { method: 'POST', headers: { 'Content-Type': 'application/json' } };
      const changeUrl = typeof theme !== 'undefined' && theme.routes?.cart_change_url
        ? theme.routes.cart_change_url
        : '/cart/change.js';

      let lastCart = cart;
      for (const item of eligible) {
        const newProperties = { ...item.properties };
        newProperties['_assembly-service'] = value;

        const body = JSON.stringify({
          id: item.key,
          quantity: item.quantity,
          properties: newProperties,
          sections: sectionsParam,
          sections_url: window.location.pathname,
        });

        const response = await fetch(changeUrl, { ...changeConfig, body });
        const text = await response.text();
        let parsed;
        try {
          parsed = JSON.parse(text);
        } catch {
          throw new Error('Failed to parse cart change response');
        }
        if (!response.ok || parsed.errors || parsed.status) {
          throw new Error(parsed.description || parsed.errors || 'Failed to update item');
        }
        lastCart = parsed;
      }

      if (eligible.length === 0) return cart;
      if (lastCart.items) return lastCart;
      return this.fetchCart();
    }

    updateToggleText(isEnabled) {
      const titleEl = this.querySelector('.assembly-service-label__title');
      const descEl = this.querySelector('.assembly-service-description');
      const container = this.closest('.cart__assembly-all');
      const strings = (typeof theme !== 'undefined' && theme.strings) || {};
      if (titleEl) {
        titleEl.textContent = isEnabled
          ? (strings.assemblyAdded || 'Montage hinzugefügt')
          : (strings.assemblyAdd || 'Montage hinzufügen');
      }
      if (descEl) {
        descEl.textContent = isEnabled
          ? (strings.assemblyAddedDescription || 'Deaktivieren Sie das Kontrollkästchen, um den Montageservice zu entfernen.')
          : (strings.assemblyAddDescription || 'Wir montieren die Möbel für Sie.');
      }
      if (container) {
        container.classList.toggle('active', !!isEnabled);
      }
    }

    collectSectionsToUpdate() {
      const sectionsToUpdate = new Set();
      document.querySelectorAll('main-cart').forEach((el) => {
        const id = el.getAttribute('data-section-id');
        if (id) sectionsToUpdate.add(id);
      });
      document.querySelectorAll('cart-drawer').forEach((el) => {
        const id = el.getAttribute('data-section-id');
        if (id) sectionsToUpdate.add(id);
      });
      const event = new CustomEvent('cart:bundled-sections', {
        bubbles: true,
        detail: { sections: Array.from(sectionsToUpdate) },
      });
      document.documentElement.dispatchEvent(event);
      return new Set(event.detail.sections);
    }

    showLoading() {
      const loader = this.querySelector('.assembly-service-loader');
      const label = this.querySelector('.assembly-service-label');
      if (loader) {
        loader.hidden = false;
        loader.style.display = 'inline-flex';
      }
      if (label) label.style.opacity = '0.5';
      if (this.checkbox) this.checkbox.disabled = true;
    }

    hideLoading() {
      const loader = this.querySelector('.assembly-service-loader');
      const label = this.querySelector('.assembly-service-label');
      if (loader) {
        loader.hidden = true;
        loader.style.display = 'none';
      }
      if (label) label.style.opacity = '1';
      if (this.checkbox) this.checkbox.disabled = false;
    }

    disableCartInteractions() {
      document.dispatchEvent(new CustomEvent('cart:disable-items', { bubbles: true }));
    }

    enableCartInteractions() {
      document.dispatchEvent(new CustomEvent('cart:enable-items', { bubbles: true }));
    }
  };

  customElements.define('assembly-service-component', AssemblyServiceComponent);

  document.addEventListener('cart:updated', (e) => {
    if (e.detail?.source === 'assembly-service') return;
    document.querySelectorAll('assembly-service-component[data-assembly-all]').forEach((el) => {
      if (el.syncStateFromCart) el.syncStateFromCart();
    });
  });
}
